import tippy from "tippy.js"
import {SvgPage} from "./SvgPage"
import {SpeedyGClientApi} from "./SpeedyGClientApi"
import "tippy.js/dist/tippy.css"
import "tippy.js/animations/shift-toward.css"
import Resources from "../../resourcesTmp/SpeedyGResources"
import navigatorLanguages from "navigator-languages"

export class SpeedyGApp {
  constructor(config, uiElems) {
    this.config = config
    this.uiElems = uiElems
    this.svgPages = []
    this.currentPageIndex = 0
    this.embedState = "unloaded"
    this.fullScreen = false
    this.fullScreenSupported = supportsFullScreen(this.appBody)
    this.messagesForEmbed = []
    this.resources = Resources
    this.languages = navigatorLanguages() || []
    this.urlExpired = false
    this.refreshUrlTimeout = this.setUrlTimeout()

    this.initializeGoogleAnalytics()
    this.initializeElements()
  }

  get svgUris() {
    return this.config.svgUris
  }

  get embedUri() {
    return this.config.embedUri
  }

  get preview() {
    return this.config.preview
  }

  get watermark() {
    return this.config.watermark
  }

  get personalization() {
    return this.config.personalization
  }

  get disablePrinting() {
    return this.config.disablePrinting
  }

  get googleAnalyticsTracker() {
    return this.config.googleAnalyticsTracker
  }

  get appBody() {
    return this.uiElems.appBody
  }

  get embedContainer() {
    return this.uiElems.embedContainer
  }

  get embedIframe() {
    return this.uiElems.embedIframe
  }

  get svgContainer() {
    return this.uiElems.svgContainer
  }

  get svgContainerHeader() {
    return this.uiElems.svgContainerHeader
  }

  get pagesContainer() {
    return this.uiElems.pagesContainer
  }

  get fullScreenButton() {
    return this.uiElems.fullScreenButton
  }

  get playButton() {
    return this.uiElems.playButton
  }
  
  get printButton() {
    return this.uiElems.printButton
  }
  
  get printButtonContainer() {
    return this.uiElems.printButtonContainer
  }
  
  get playbackButton() {
    return this.uiElems.playbackButton
  }
  
  get scoreButton() {
    return this.uiElems.scoreButton
  }
  
  get pageTurnerBack() {
    return this.uiElems.pageTurnerBack
  }

  get pageTurnerForward() {
    return this.uiElems.pageTurnerForward
  }

  get overlayCaption() {
    return this.uiElems.overlayCaption
  }

  get mainPage() {
    return this.uiElems.mainPage
  }

  get spinnerElem() {
    return this.uiElems.spinner
  }

  get pageCount() {
    return this.svgPages.length
  }

  get currentPage() {
    if (this.currentPageIndex < this.pageCount) {
      return this.svgPages[this.currentPageIndex]
    }
    else {
      return null
    }
  }

  // Reads the value of the given resource.  See generateResources.js
  // for an explanation of where the resources come from and how
  // they're structured.
  resource(name) {
    // Search through the specific locales
    for(const language of this.languages) {
      if (this.resources[language] && this.resources[language][name]) {
        return this.resources[language][name]
      }
    }

    // Search the default
    if (this.resources.default[name] != null) {
      return this.resources.default[name]
    }

    // Return a default
    return `[${name}]`
  }

  // This code is taken from
  // https://developers.google.com/analytics/devguides/collection/analyticsjs
  initializeGoogleAnalytics() {
    (function(i,s,o,g,r,a,m){i['GoogleAnalyticsObject']=r;i[r]=i[r]||function(){(i[r].q=i[r].q||[]).push(arguments)},i[r].l=1*new Date();a=s.createElement(o),m=s.getElementsByTagName(o)[0];a.async=1;a.src=g;m.parentNode.insertBefore(a,m)})(window,document,'script','https://www.google-analytics.com/analytics.js','ga');
    ga('create', this.googleAnalyticsTracker, 'auto');
    ga('send', 'pageview');
  }

  reportGAEvent(name) {
    try {
      ga("send", "event", "SVG Embed Clicks", document.referrer, name)
    }
    catch(e) {}
  }

  initializeElements() {
    this.overlayCaption.appendChild(document.createTextNode(this.resource("content.loadingInteractiveScore")))
    this.addTimestamp()
  }

  setUrlTimeout() {
    const signedUrlTimeout = 1000 * 60 * 14;
    return setTimeout(()=> this.urlExpired = true, signedUrlTimeout)
  }

  addTimestamp() {
    const comment = document.createComment(process.env.VERSION);
    this.appBody.insertBefore(comment, this.appBody.firstChild);
  }

  async start() {
    this.fullScreenButton.addEventListener("click", ()=>this.onFullScreenButtonClick())
    this.playButton.addEventListener("click", ()=>this.play())
    if (!this.disablePrinting) {
      this.printButton.addEventListener("click", ()=>this.print())
    }
    this.playbackButton.addEventListener("click", ()=>this.playback())
    this.scoreButton.addEventListener("click", ()=>this.notes())
    this.appBody.addEventListener("keypress", e=>this.onKeypress(e))
    this.appBody.addEventListener("keydown", e=>this.onKeydown(e))
    this.pageTurnerBack.addEventListener("click", ()=>this.onPageTurnerBackClick())
    this.pageTurnerForward.addEventListener("click", ()=>this.onPageTurnerForwardClick())

    addTippy(this.playButton, this.resource("palette.play"))
    addTippy(this.playbackButton, this.resource("palette.playback"))
    addTippy(this.scoreButton, this.resource("palette.score"))
    if (!this.disablePrinting) {
      addTippy(this.printButton, this.preview ? this.resource("palette.printPdfSample"): this.resource("palette.printPdf"))
    }
    addTippy(this.fullScreenButton, this.resource("palette.fullScreen"))

    for(let pageIndex = 0; pageIndex < this.svgUris.length; pageIndex++) {
      const uri = this.svgUris[pageIndex]
      const pageElem = document.createElement("div")
      const pageElemId = `page-${pageIndex + 1}`
      pageElem.setAttribute("id", pageElemId)
      this.pagesContainer.appendChild(pageElem)
      const includeWatermark = this.watermark && pageIndex > 0
      const personalization = this.personalization
      const svgPage = new SvgPage(uri, pageElem, pageIndex, includeWatermark, personalization, ()=>this.onPageLoaded())
      this.svgPages.push(svgPage)
    }
    if (this.svgPages.length > 0) {
      this.svgPages[0].shouldDisplay = true
    }

    for(const svgPage of this.svgPages) {
      svgPage.load().then(() => {
        if (this.embedState === "svgLoaded") return
        this.embedState = "svgLoaded"
        this.updateElements()
      })
    }

    window.addEventListener("resize", ()=>this.onResize())
    this.adjustToSize()

    this.listenForEmbedMessages()
    this.listenForFullScreenMessages()
    new SpeedyGClientApi(this)

    this.updateElements()

    // If we find that there are no generated SVG pages, then just
    // switch to interactive mode.  There may be some cases where
    // SVG's failed to generate for the preview and/or the full mode
    if (this.svgPages.length === 0) {
      this.switchToEmbed(null)
    }
  }

  listenForEmbedMessages() {
    window.document.addEventListener("editorPubSubEvent", e=>{
      switch(this.publishedEventType(e)) {
      case "SCORE_LOADED": 
        if (this.embedState === "loading") {
          this.embedState = "loaded"
          this.updateElements()
          this.sendQueuedEmbedMessages()
        }
        break
      case "EMBED.REQUEST_ENTER_FULLSCREEN":
        this.showFullScreen()
        break
      case "EMBED.REQUEST_EXIT_FULLSCREEN":
        this.exitFullScreen()
        break
      }
    }, false)
  }

  publishedEventType(e) {
    if (e == null) {
      return null
    }
    if (e.detail == null) {
      return null
    }
    return e.detail.eventType
  }

  listenForParentMessages() {
    window.addEventListener("message", e=>{
      const eventType = e && e.data && e.data.eventType;
      switch(eventType) {
      case "PREV_PAGE":
        this.showPrevPage()
        break;
      case "NEXT_PAGE":
        this.showNextPage()
        break;
      }
    }, false)
  }

  listenForFullScreenMessages() {
    document.addEventListener('fullscreenchange', e=>this.onFullScreenChange(e))
    document.addEventListener('webkitfullscreenchange', e=>this.onFullScreenChange(e))
    document.addEventListener('mozfullscreenchange', e=>this.onFullScreenChange(e))
    document.addEventListener('MSFullscreenChange', e=>this.onFullScreenChange(e))
  }

  onFullScreenChange(e) {
    if (isFullscreen()) {
      this.fullScreen = true
    }
    else {
      this.fullScreen = false
    }
    this.notifyEmbedOfFullScreenState()
    this.updateElements()
  }

  notifyEmbedOfFullScreenState() {
    if (this.embedState !== "unloaded") {
      if (isFullscreen()) {
        this.sendEmbedMessage("EMBED.NOTIFY_ENTERED_FULLSCREEN")
      }
      else {
        this.sendEmbedMessage("EMBED.NOTIFY_EXITED_FULLSCREEN")
      }
    }
  }

  sendEmbedMessage(msg) {
    this.messagesForEmbed.push(msg)
    this.sendQueuedEmbedMessages()
  }

  async sendQueuedEmbedMessages() {
    if (this.embedState === "loaded") {
      const messagesForEmbed = this.messagesForEmbed
      this.messagesForEmbed = []
      for(const msg of messagesForEmbed) {
        this.dispatchEmbedEvent(msg)
      }
    }
  }

  dispatchEmbedEvent(msg) {
    const event = new CustomEvent("editorPubSubEvent", {detail: {eventType: msg}})
    this.embedIframe.contentDocument.dispatchEvent(event)
  }

  onPageTurnerBackClick() {
    this.showPrevPage()
  }

  onPageTurnerForwardClick() {
    this.showNextPage()
  }

  onFullScreenButtonClick() {
    // Note that our internal state won't update until
    // onFullScreenChange is called by the browser
    if (this.fullScreen) {
      this.reportGAEvent("Close Full Screen")
      this.exitFullScreen()
    }
    else {
      this.reportGAEvent("Open Full Screen")
      this.showFullScreen()
    }
  }

  onResize() {
    this.adjustToViewportSize()
  }

  onPageLoaded() {
    this.adjustToViewportSize()
  }

  showPrevPage() {
    if (this.embedState === "loaded") {
      this.sendEmbedMessage("SCORE_PREV_PAGE")
    }
    else if (this.hasPrevPage && this.embedState === "svgLoaded") {
      this.currentPageIndex--
      this.updateElements()
    }
  }

  showNextPage() {
    if (this.embedState === "loaded") {
      this.sendEmbedMessage("SCORE_NEXT_PAGE")
    }
    else if (this.hasNextPage && this.embedState === "svgLoaded") {
      this.currentPageIndex++
      this.updateElements()
    }
  }

  play() {
    this.reportGAEvent("Play")
    this.switchToEmbed("PLAYBACK_START_EXTERNAL")
  }

  playback() {
    this.reportGAEvent("Open Playback")
    this.switchToEmbed("OPEN_PLAYBACK_DIALOG")
  }

  notes() {
    this.reportGAEvent("Open Score")
    this.switchToEmbed("OPEN_EMBEDDED_SCORE_DIALOG")
  }

  print() {
    this.reportGAEvent("Open Print/PDF")
    this.switchToEmbed("OPEN_PRINT_DIALOG")
  }

  switchToEmbed(embedMessage) {
    clearTimeout(this.refreshUrlTimeout);
    if (this.urlExpired) {
      this.sendEmbedMessage("EMBED.URL_EXPIRED")
    }
    
    if (this.embedState === "unloaded" || this.embedState === "svgLoaded") {
      this.reportGAEvent("Enter Interactive Mode")
      // FIXME - this depends on the token not expiring.  Perhaps a
      // better way to go is to load the embed up-front, but only
      // enough for the editor to load and get the score content
      // before the token expires.  Or... if the token has expired
      // just have the user reload the page.
      this.embedIframe.src = this.embedUri
      this.embedState = "loading"
      this.updateElements()

      this.sendEmbedMessage("EMBED.CONTROL_FULLSCREEN_EXTERNALLY")
      this.notifyEmbedOfFullScreenState()

      if (embedMessage != null) {
        this.sendEmbedMessage(embedMessage)
      }
    }
  }

  async showFullScreen() {
    return await requestFullscreen(this.mainPage)
  }

  async exitFullScreen() {
    return await requestExitFullscreen()
  }

  adjustToViewportSize() {
    const viewportWidth = Math.max(
      document.documentElement.clientWidth || 0,
      window.innerWidth || 0
    )
    const viewportHeight = Math.max(
      document.documentElement.clientHeight || 0,
      window.innerHeight || 0
    )
    const headerHeight = this.svgContainerHeader.offsetHeight
    this.adjustToSize(viewportWidth, viewportHeight - headerHeight)
  }

  adjustToSize(width, height) {
    // FIXME - account for the header size, both in and not in full size mode
    const page = this.currentPage
    if (page && page.svgWidth != null && page.svgHeight != null && width != null && height != null) {

      const hscale = width / page.svgWidth
      const vscale = height / page.svgHeight
      const scale = Math.min(hscale, vscale)
      page.setScale(scale)
    }
  }

  get hasNextPage() {
    return this.currentPageIndex < this.pageCount - 1
  }

  get hasPrevPage() {
    return this.currentPageIndex > 0
  }

  onKeypress(e) {
  }

  onKeydown(e) {
    switch(e.key) {
      case "ArrowLeft":
      case "ArrowUp":
      case "PageUp":
        this.showPrevPage();
        break;
      case "ArrowRight":
      case "ArrowDown":
      case "PageDown":
        this.showNextPage();
        break;
    }
  }

  updateElements() {
    if (this.embedState === "loading" || this.embedState == "loaded") {
      this.embedContainer.classList.remove("hidden")
    }
    else {
      this.embedContainer.classList.add("hidden")
    }
    if (this.embedState === "loaded") {
      this.svgContainer.classList.add("hidden")
    }
    else {
      this.svgContainer.classList.remove("hidden")
    }

    if (this.embedState === "loading") {
      this.spinnerElem.classList.remove("hidden")
    }
    else {
      this.spinnerElem.classList.add("hidden")
    }

    if (this.fullScreenSupported) {
      this.fullScreenButton.classList.add("paletteButton")
      this.fullScreenButton.classList.remove("hidden")
    }
    else {
      this.fullScreenButton.classList.add("hidden")
      this.fullScreenButton.classList.remove("paletteButton")
    }
    
    if (this.fullScreen) {
      this.fullScreenButton.classList.remove("fullScreenExpandSmall")
      this.fullScreenButton.classList.add("fullScreenCompressSmall")
      this.svgContainerHeader.classList.add('fullScreen')
    }
    else {
      this.fullScreenButton.classList.remove("fullScreenCompressSmall")
      this.fullScreenButton.classList.add("fullScreenExpandSmall")
      this.svgContainerHeader.classList.remove('fullScreen')
    }

    if (this.disablePrinting) {
      this.printButtonContainer.classList.add("hidden")
      this.printButtonContainer.classList.remove("displayed-inline")
    }
    else {
      this.printButtonContainer.classList.add("displayed-inline")
      this.printButtonContainer.classList.remove("hidden")
    }

    for(const svgPage of this.svgPages) {
      const elem = svgPage.elem
      if (svgPage.pageIndex === this.currentPageIndex) {
        elem.classList.remove("hidden")
        svgPage.shouldDisplay = true
        svgPage.updateElements()
      }
      else {
        elem.classList.add("hidden")
        svgPage.shouldDisplay = false
        svgPage.updateElements()
      }
    }

    if (this.hasPrevPage && this.embedState === "svgLoaded") {
      this.pageTurnerBack.classList.add("showing")
    }
    else {
      this.pageTurnerBack.classList.remove("showing")
    }

    if (this.hasNextPage && this.embedState === "svgLoaded") {
      this.pageTurnerForward.classList.add("showing")
    }
    else {
      this.pageTurnerForward.classList.remove("showing")
    }

    this.adjustToViewportSize()
  }
}



async function requestFullscreen(element = document) {
  return await polyFullscreen(getFullscreenFunction(element), element);
}

function getFullscreenFunction(element) {
  return element.requestFullscreen ||
    element.mozRequestFullScreen ||
    element.webkitRequestFullscreen ||
    element.msRequestFullscreen;
}

function supportsFullScreen(element) {
  const f = getFullscreenFunction(element)
  return typeof(f) === "function"
}

async function polyFullscreen(f, element) {
  if (f == null) {
    throw new Error(`No fullscreen function available`)
  }
  return await f.call(element)
}

function isFullscreen() {
  return document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.webkitCurrentFullScreenElement
}

function getExitFullscreenFunction() {
  return document.exitFullScreen ||
    document.mozCancelFullScreen ||
    document.webkitExitFullscreen ||
    document.msExitFullscreen;
}

async function requestExitFullscreen() {
  return await polyExitFullscreen(getExitFullscreenFunction())
}

async function polyExitFullscreen(f) {
  if (f == null) {
    throw new Error(`No exitfullscreen function available`)
  }
  return await f.call(document)
}

function addTippy(elem, text) {
  tippy(elem, {
    delay: [500, 50],
    placement: "bottom",
    appendTo: "parent",
    arrow: true,
    animation: "shift-toward",
    theme: "tippyPaletteButton",
    content: text,
  })
}
